import React, { useState, useEffect, useCallback } from 'react';
import { api, getApiErrorMessage } from '../lib/api';

const SPEAKER_CONFIG = {
  host: { label: 'HOST', color: '#A1A1AA', bg: '#A1A1AA10' },
  nova: { label: 'NOVA', color: '#e8a598', bg: '#e8a59810' },
  orion: { label: 'ORION', color: '#00E5FF', bg: '#00E5FF10' },
};

const SEV_COLORS = {
  nominal: '#00E5FF',
  elevated: '#FF9F0A',
  storm: '#FF6B35',
  critical: '#FF3B30',
};

const MON_COLORS = { LIVE: '#34C759', DEGRADED: '#FF9F0A', PARTIAL_COVERAGE: '#FF9F0A', OFFLINE: '#FF3B30' };

export default function ShowDashboard({ onSwitchAgent }) {
  const [episode, setEpisode] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [compact, setCompact] = useState(false);

  const loadEpisodes = useCallback(async () => {
    try {
      const res = await api.get('/api/show/episodes?limit=10');
      setEpisodes(res.data.episodes || []);
    } catch {}
  }, []);

  const loadLatest = useCallback(async () => {
    try {
      const res = await api.get('/api/show/latest');
      if (res.data && !res.data.error) setEpisode(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadEpisodes();
    loadLatest();
  }, [loadEpisodes, loadLatest]);

  const handleGenerate = useCallback(async () => {
    setGenerating(true);
    try {
      const res = await api.post('/api/show/episode', null, { timeout: 90000 });
      setEpisode(res.data);
      loadEpisodes();
    } catch (e) {
      console.error('Episode generation failed:', getApiErrorMessage(e, 'Unable to generate episode'));
    } finally {
      setGenerating(false);
    }
  }, [loadEpisodes]);

  const handleLoadEpisode = useCallback(async (id) => {
    try {
      const res = await api.get(`/api/show/episode/${id}`);
      if (res.data?.episode) setEpisode(res.data.episode);
    } catch {}
  }, []);

  const sevColor = SEV_COLORS[episode?.overallSeverity || episode?.overall_severity] || '#00E5FF';
  const monState = episode?.monitoringState || episode?.monitoring_state || 'LIVE';
  const monColor = MON_COLORS[monState] || '#52525B';
  const segments = episode?.segments || episode?.transcript || [];
  const briefing = episode?.finalBriefing || episode?.final_briefing || '';
  const isPartial = episode?.partialMode || episode?.partial_mode;
  const conf = episode?.overallConfidence || episode?.overall_confidence || 0;
  const sev = episode?.overallSeverity || episode?.overall_severity || 'nominal';

  return (
    <div className="show-app" data-testid="show-app">
      {/* Top bar */}
      <div className="show-topbar" data-testid="show-topbar">
        <div className="show-topbar-logo">INTELLIGENCE BRIEFING</div>
        <div className="show-topbar-sep" />
        <div className="show-topbar-sub">MULTI-AGENT SHOW</div>
        <div className="show-topbar-spacer" />
        {episode && (
          <>
            <div className="show-topbar-badge" style={{ color: monColor, borderColor: `${monColor}40` }} data-testid="show-monitoring-state">
              {monState.replace('_', ' ')}
            </div>
            <div className="show-topbar-badge" style={{ color: sevColor, borderColor: `${sevColor}40` }} data-testid="show-severity-badge">
              {sev.toUpperCase()}
            </div>
            <div className="show-topbar-metric" data-testid="show-confidence">
              <span className="show-metric-val">{conf.toFixed(0)}</span>
              <span className="show-metric-label">CONF</span>
            </div>
            {isPartial && <span className="show-partial-badge" data-testid="show-partial-badge">PARTIAL</span>}
          </>
        )}
        <div className="show-topbar-sep" />
        <button className="show-switch-btn" onClick={() => onSwitchAgent('nova')} data-testid="switch-to-nova-from-show">NOVA</button>
        <button className="show-switch-btn" onClick={() => onSwitchAgent('orion')} data-testid="switch-to-orion-from-show">ORION</button>
        <button className="show-switch-btn" onClick={() => onSwitchAgent('vega')} data-testid="switch-to-vega-from-show" style={{ color: '#6366F1', borderColor: '#6366F130' }}>VEGA</button>
        <button className="show-switch-btn" onClick={() => onSwitchAgent('atlas')} data-testid="switch-to-atlas-from-show" style={{ color: '#059669', borderColor: '#05966930' }}>ATLAS</button>
        <button className="show-switch-btn" onClick={() => onSwitchAgent('analytics')} data-testid="switch-to-analytics-from-show" style={{ color: '#14B8A6', borderColor: '#14B8A630' }}>ANALYTICS</button>
      </div>

      <div className="show-body">
        {/* Left sidebar — episode list + controls */}
        <div className="show-sidebar" data-testid="show-sidebar">
          <button
            className="show-gen-btn"
            onClick={handleGenerate}
            disabled={generating}
            data-testid="show-generate-btn"
          >
            {generating ? 'GENERATING EPISODE...' : 'NEW EPISODE'}
          </button>

          <div className="show-section-label">RECENT EPISODES</div>
          <div className="show-ep-list" data-testid="show-episode-list">
            {episodes.map(ep => {
              const epSev = ep.overall_severity || 'nominal';
              const epColor = SEV_COLORS[epSev] || '#52525B';
              return (
                <div
                  key={ep.id}
                  className={`show-ep-item ${ep.id === (episode?.id || episode?.episodeId) ? 'active' : ''}`}
                  onClick={() => handleLoadEpisode(ep.id)}
                  data-testid={`episode-item-${ep.id}`}
                >
                  <div className="show-ep-dot" style={{ background: epColor }} />
                  <div className="show-ep-info">
                    <span className="show-ep-time">{new Date(ep.created_at).toLocaleTimeString()}</span>
                    <span className="show-ep-sev" style={{ color: epColor }}>{epSev.toUpperCase()}</span>
                  </div>
                  {ep.partial_mode && <span className="show-ep-partial">P</span>}
                </div>
              );
            })}
            {episodes.length === 0 && <div className="show-empty">No episodes yet</div>}
          </div>

          <button className="show-compact-btn" onClick={() => setCompact(!compact)} data-testid="show-compact-toggle">
            {compact ? 'FULL VIEW' : 'COMPACT'}
          </button>
        </div>

        {/* Main content — transcript + briefing */}
        <div className="show-main" data-testid="show-main">
          {!episode ? (
            <div className="show-empty-main">
              <div className="show-empty-title">NO EPISODE LOADED</div>
              <div className="show-empty-sub">Click NEW EPISODE to generate a combined briefing from Nova and Orion</div>
            </div>
          ) : (
            <>
              {/* Partial mode banner */}
              {isPartial && (
                <div className="show-partial-banner" data-testid="show-partial-banner">
                  PARTIAL BRIEFING — {episode?.partialReason || episode?.partial_reason || 'One or more agents unavailable'}
                </div>
              )}

              {/* Transcript */}
              <div className={`show-transcript ${compact ? 'compact' : ''}`} data-testid="show-transcript">
                {segments.map((seg, i) => {
                  const sp = SPEAKER_CONFIG[seg.speaker] || SPEAKER_CONFIG.host;
                  return (
                    <div key={i} className="show-segment" data-testid={`show-segment-${i}`}>
                      <div className="show-seg-header">
                        <span className="show-seg-speaker" style={{ color: sp.color }}>{sp.label}</span>
                        <span className="show-seg-type">{seg.type}</span>
                        {seg.title && <span className="show-seg-title">{seg.title}</span>}
                      </div>
                      <div className="show-seg-text" style={{ borderLeftColor: sp.color }}>
                        {seg.text || seg.rendered_text}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Final combined briefing */}
              {briefing && (
                <div className="show-briefing" data-testid="show-final-briefing">
                  <div className="show-section-label">COMBINED BRIEFING</div>
                  <div className="show-briefing-text">{briefing}</div>
                  <div className="show-briefing-meta">
                    <span style={{ color: sevColor }}>{sev.toUpperCase()}</span>
                    <span>Confidence: {conf.toFixed(0)}%</span>
                    <span>Agents: {(episode?.agentsIncluded || episode?.agents_included || []).join(' + ')}</span>
                    <button
                      className="show-copy-btn"
                      onClick={() => navigator.clipboard.writeText(briefing)}
                      data-testid="show-copy-briefing"
                    >COPY</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
